<script setup lang="ts">
import type { ShipexkursTab } from '~~/types';
import type { WikiVehicle } from '~~/types/gamedata';

const props = defineProps({
  raw: {
    type: Object as () => WikiVehicle,
    required: true,
  },
});

type PerformanceStat = {
  name: string;
  max: number;
  suffix: string;
  value: number;
  boostedValue: number;
};

// Nicht jedes Schiff hat alle Kennzahlen (z.B. kein Schild oder Frachtraster)
// — daher werden fehlende Werte herausgefiltert statt sie als 0 zu werten,
// damit das Radar je Schiff eine unterschiedliche Achsenzahl (3, 5, 6, ...)
// haben kann. Maxima orientieren sich an den bereits in
// `sidebar-performance.vue` verwendeten Skalen (Geschwindigkeit/Agilität).
// `getBoosted` ist optional — fehlt eine geboostete Variante (z.B. Hülle,
// Schild, Fracht), zeigt die zweite Serie denselben Wert wie die erste.

// ===PERFORMANCE_STAT_DEFS===
const PERFORMANCE_STAT_DEFS: {
  name: string;
  max: number;
  suffix: string;
  get: (raw: WikiVehicle) => number | null | undefined;
  getBoosted?: (raw: WikiVehicle) => number | null | undefined;
}[] = [
  { name: "Lenkwinkelgeschwindigkeit im Stehen", max: 300, suffix: "°/Sek.", get: (raw) => raw.agility?.roll, getBoosted: (raw) => raw.agility?.roll_boosted },
  { name: "Lenkwinkelgeschwindigkeit bei 40 m/s", max: 300, suffix: "°/Sek.", get: (raw) => raw.agility?.pitch, getBoosted: (raw) => raw.agility?.pitch_boosted },
  { name: "Max. Lenkwinkel im stehen", max: 60, suffix: "°", get: (raw) => raw.agility?.pitch, getBoosted: (raw) => raw.agility?.pitch_boosted },
  { name: "Max. Lenkwinkel bei 40 m/s", max: 300, suffix: "°/Sek.", get: (raw) => raw.agility?.pitch, getBoosted: (raw) => raw.agility?.pitch_boosted },
  { name: "Lenkrückstellungsgeschwindigkeit", max: 300, suffix: "°/Sek.", get: (raw) => raw.agility?.pitch, getBoosted: (raw) => raw.agility?.pitch_boosted },
];

const performanceStats = computed<PerformanceStat[]>(() =>
  PERFORMANCE_STAT_DEFS
    .map((def) => {
      const value = def.get(props.raw);
      if (value == null) return null;
      const boostedValue = def.getBoosted?.(props.raw) ?? value;
      return { name: def.name, max: def.max, suffix: def.suffix, value, boostedValue };
    })
    .filter((stat): stat is PerformanceStat => stat !== null),
);

const performanceIndicators = computed(() =>
  performanceStats.value.map((stat) => ({ name: stat.name, max: stat.max, suffix: stat.suffix })),
);

const performanceSeries = computed(() => [
  {
    name: "Normal",
    values: performanceStats.value.map((stat) => stat.value),
    color: "primary",
  },
  {
    name: "Geboostet",
    values: performanceStats.value.map((stat) => stat.boostedValue),
    color: "secondary",
  },
]);

type BoostMultiplierStat = {
  name: string;
  max: number;
  suffix: string;
  value: number;
};

// Nicht in `types/gamedata.ts` typisiert (kommt aus `WikiItem`s Catch-all
// `[key: string]: unknown`) — hier nur die tatsächlich genutzten Felder.
type FlightControllerBoostAxis = { positive?: number | null; negative?: number | null } | null;
type FlightController = {
  boost_multiplier?: {
    accel_x?: FlightControllerBoostAxis;
    accel_y?: FlightControllerBoostAxis;
    accel_z?: FlightControllerBoostAxis;
    pitch?: number | null;
    yaw?: number | null;
    roll?: number | null;
    pitch_accel?: number | null;
    yaw_accel?: number | null;
    roll_accel?: number | null;
  } | null;
  boost_activation?: {
    pre_delay_time?: number | null;
    ramp_up_time?: number | null;
    ramp_down_time?: number | null;
  } | null;
};

const flightController = computed(
  () =>
    props.raw.ports?.find((port) => port.name === "hardpoint_controller_flight")?.equipped_item
      ?.flight_controller as FlightController | undefined,
);

// `boost_multiplier`-Werte sind kleine Faktoren (~1.0–1.6), keine Grad/Sek.-
// Werte wie die Agility-Stats oben — eigene Skala und Suffix.
const PERFORMANCE_BOOST_MULT_DEFS: {
  name: string;
  max: number;
  suffix: string;
  get: (flightController: FlightController | undefined) => number | null | undefined;
}[] = [
  { name: "Vorwärts", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.accel_y?.positive },
  { name: "Rückwärts", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.accel_y?.negative },
  { name: "Strafe (Hoch)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.accel_z?.positive },
  { name: "Strafe (Runter)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.accel_z?.negative },
  { name: "Strafe (Links/Rechts)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.accel_x?.positive },
  { name: "Neigen", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.pitch },
  { name: "Gieren", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.yaw },
  { name: "Rollen", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.roll },
  { name: "Neigen (Beschl.)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.pitch_accel },
  { name: "Gieren (Beschl.)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.yaw_accel },
  { name: "Rollen (Beschl.)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.roll_accel },
];

const performanceBoostMultiplierStats = computed<BoostMultiplierStat[]>(() =>
  PERFORMANCE_BOOST_MULT_DEFS
    .map((def) => {
      const value = def.get(flightController.value);
      if (value == null) return null;
      return { name: def.name, max: def.max, suffix: def.suffix, value };
    })
    .filter((stat): stat is BoostMultiplierStat => stat !== null),
);

const performanceBoostMultiplierIndicators = computed(() =>
  performanceBoostMultiplierStats.value.map((stat) => ({ name: stat.name, max: stat.max, suffix: stat.suffix })),
);

const performanceBoostMultiplierSeries = computed(() => [{
  name: "Boost-Multiplikator",
  values: performanceBoostMultiplierStats.value.map((stat) => stat.value),
  color: "primary",
}]);

function formatNumber(value: number, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits }).format(value);
}

// Triebwerks-Performance: G-Kraft je Triebwerkstyp (bei Gravlev-Fahrzeugen
// mit Hover-Triebwerken; reine Radfahrzeuge liefern hier ein leeres Array
// und die Sektion blendet sich unten aus) — ein Balken pro Typ, eine Farbe.
const THRUSTER_TYPE_LABELS: Record<string, string> = {
  Main: "Haupt",
  Maneuver: "Manöver",
  Retro: "Retro",
  Vtol: "VTOL",
  VTOL: "VTOL",
};

const thrusterStats = computed(() =>
  (props.raw.propulsion?.thrusters ?? [])
    .filter((thruster) => thruster.g != null)
    .map((thruster) => ({ label: THRUSTER_TYPE_LABELS[thruster.type] ?? thruster.type, g: thruster.g! })),
);

// Für flächige Balken (anders als dünne Linien/15%-Flächen im Radar-Chart)
// markiert die dataviz-Palettenvalidierung die App-Standardfarben
// (aris-400/industrial-400, sehr hell) als zu hell für den Lightness-Band-
// Check grosser Flächen — diese dunkleren Stufen derselben Farbfamilie
// bestehen `validate_palette.js "#009189,#c34b1c" --mode dark` sauber.
const BAR_COLOR_PRIMARY = "#009189";
const BAR_COLOR_SECONDARY = "#c34b1c";

const thrusterGForceCategories = computed(() => thrusterStats.value.map((stat) => stat.label));
const thrusterGForceSeries = computed(() => [
  { name: "G-Kraft", values: thrusterStats.value.map((stat) => stat.g), color: BAR_COLOR_PRIMARY },
]);

// Beschleunigung & Bremsung: zwei Gruppen (Beschleunigen/Bremsen) über feste
// Farben unterschieden, mit `null` an der jeweils anderen Kategorie (ECharts
// lässt den Balken dort schlicht aus).
type AccelGroup = "accel" | "brake";
const ACCEL_DEFS: { label: string; group: AccelGroup; get: (raw: WikiVehicle) => number | null | undefined }[] = [
  { label: "0 → SCM", group: "accel", get: (raw) => raw.speed?.zero_to_scm },
  { label: "0 → Max", group: "accel", get: (raw) => raw.speed?.zero_to_max },
  { label: "SCM → 0", group: "brake", get: (raw) => raw.speed?.scm_to_zero },
  { label: "Max → 0", group: "brake", get: (raw) => raw.speed?.max_to_zero },
];

const accelStats = computed(() =>
  ACCEL_DEFS
    .map((def) => {
      const value = def.get(props.raw);
      return value == null ? null : { label: def.label, group: def.group, value };
    })
    .filter((stat): stat is { label: string; group: AccelGroup; value: number } => stat !== null),
);

const accelCategories = computed(() => accelStats.value.map((stat) => stat.label));
const accelSeries = computed(() => [
  {
    name: "Beschleunigung",
    values: accelStats.value.map((stat) => (stat.group === "accel" ? stat.value : null)),
    color: BAR_COLOR_PRIMARY,
  },
  {
    name: "Bremsung",
    values: accelStats.value.map((stat) => (stat.group === "brake" ? stat.value : null)),
    color: BAR_COLOR_SECONDARY,
  },
]);

// Sonstige: Quantenantrieb (bei Bodenfahrzeugen praktisch immer leer, siehe
// oben) & Versicherung — heterogene Einzelwerte, dafür Stat-Kacheln statt
// eines Diagramms (siehe dataviz "is it even a chart?").
interface MiscStat {
  label: string;
  value: string;
  unit?: string;
}

// Geschwindigkeits-Graph + Antriebsdaten, vormals in einer eigenen
// Sidebar-Karte (`sidebar-speed-ground.vue`, entfernt) — jetzt Teil des
// Performance-Tabs.
const speedTabs = computed<ShipexkursTab[]>(() => [{
  label: "Bodenfahrzeug",
  icon: "i-lucide-gauge",
  items: [{
    label: "Geschwindigkeiten (m/s)",
    graph: "circle",
    full: true,
    values: [{
      label: "Höchstgeschwindigkeit",
      value: Math.round(props.raw.drive?.max_speed_ms ?? 0),
      min: 0,
      max: 40,
      color: "primary",
    }, {
      label: "Rückwärtsgeschwindigkeit",
      value: Math.round(props.raw.drive?.reverse_speed_ms ?? 0),
      min: 0,
      max: 40,
      color: "secondary",
    }],
  }, {
    label: "Höchstgeschwindigkeit",
    value: Math.round(props.raw.drive?.max_speed_kph ?? 0),
    suffix: "km/h",
  }, {
    label: "Rückwärtsgeschwindigkeit",
    value: Math.round(props.raw.drive?.reverse_speed_kph ?? 0),
    suffix: "km/h",
  }, {
    label: "Reifenanzahl",
    value: props.raw?.drive?.wheels?.count ?? 0,
  }, {
    label: "Antriebart",
    value: (props.raw?.drive?.wheels?.drive_type === "AWD" ? "Allradantrieb" : props.raw?.drive?.wheels?.drive_type === "FWD" ? "Frontantrieb" : props.raw?.drive?.wheels?.drive_type === "RWD" ? "Rückantrieb" : props.raw?.drive?.wheels?.drive_type ?? "-"),
  }, {
    label: "Antriebsräder",
    value: props.raw?.drive?.wheels?.driving_count ?? 0,
  }, {
    label: "Lenkende Räder",
    value: props.raw?.drive?.wheels?.steering_count ?? 0,
  }],
}]);

// Ramp Up/Down: Ein-/Ausklingzeit des Afterburners bis zur vollen
// Schubleistung — sitzt am selben `flight_controller`-Item wie die
// Boost-Multiplikatoren oben (`boost_activation` statt `boost_multiplier`).
const rampStats = computed<MiscStat[]>(() => {
  const activation = flightController.value?.boost_activation;
  const stats: MiscStat[] = [];
  if (activation?.ramp_up_time != null) stats.push({ label: "Ramp-Up", value: formatNumber(activation.ramp_up_time), unit: "s" });
  if (activation?.ramp_down_time != null) stats.push({ label: "Ramp-Down", value: formatNumber(activation.ramp_down_time), unit: "s" });
  if (activation?.pre_delay_time != null) stats.push({ label: "Aktivierungsverzögerung", value: formatNumber(activation.pre_delay_time), unit: "s" });
  return stats;
});

const quantumStats = computed<MiscStat[]>(() => {
  const quantum = props.raw.quantum;
  const stats: MiscStat[] = [];
  if (quantum?.quantum_speed != null) stats.push({ label: "Quantengeschwindigkeit", value: formatNumber(quantum.quantum_speed / 1_000_000), unit: "Mm/s" });
  if (quantum?.quantum_spool_time != null) stats.push({ label: "Spool-Zeit", value: formatNumber(quantum.quantum_spool_time), unit: "s" });
  if (quantum?.quantum_range != null) stats.push({ label: "Reichweite", value: formatNumber(quantum.quantum_range / 1_000_000_000), unit: "Gm" });
  if (quantum?.quantum_fuel_capacity != null) stats.push({ label: "Treibstoffkapazität", value: formatNumber(quantum.quantum_fuel_capacity), unit: "SCU" });
  return stats;
});

const insuranceStats = computed<MiscStat[]>(() => {
  const insurance = props.raw.insurance;
  const stats: MiscStat[] = [];
  if (insurance?.claim_time != null) stats.push({ label: "Standard-Anspruch", value: formatNumber(insurance.claim_time), unit: "Min." });
  if (insurance?.expedite_time != null) stats.push({ label: "Express-Anspruch", value: formatNumber(insurance.expedite_time), unit: "Min." });
  if (insurance?.expedite_cost != null) stats.push({ label: "Express-Kosten", value: formatNumber(insurance.expedite_cost), unit: "aUEC" });
  return stats;
});
</script>

<template>
  <div>
    <UTabs
      orientation="vertical"
      variant="link"
      class="ml-2"
      size="sm"
      default-value="drive"
      :items="[
        { label: 'Fahrzeug-Performance', value: 'drive', slot: 'drive' },
        { label: 'Triebwerks-Performance', value: 'engine', slot: 'engine' },
        { label: 'Sonstige', value: 'other', slot: 'other' }]"
      :ui="{
        root: 'items-start',
        list: 'space-y-1',
        trigger: 'border border-default data-[state=active]:border-primary data-[state=active]:bg-white/5',
      }"
    >
      <template #drive>
        <!--
          Container Query statt sm:/lg: — der Slider sitzt in einer
          resizable USplitter-Spalte, deren Breite unabhängig vom Viewport
          ist (siehe gleiches Muster in `ship/table/tab-content.vue`).
          Unterhalb `@lg` (32rem Containerbreite) stehen die Charts
          untereinander, ab `@lg` nebeneinander, jeweils halbbreit.
        -->
        <div class="@container mx-auto w-full max-w-2xl">
          <ShipexkursShipTable :tabs="speedTabs" class="mb-4" />
          <div class="flex flex-wrap gap-6">
            <div class="w-full @lg:w-[calc(50%-0.75rem)]">
              <p class="mt-4 flex items-center justify-center gap-2 p-0 text-center text-sm font-medium">
                <ShipexkursSystemIcon name="maneuveringthruster" class="size-4 text-aris-400" />
                Schiffsachsen Rotationsgeschwindigkeit
              </p>
              <ChartsRadarChart
                v-if="performanceStats.length >= 3"
                :height="260"
                :indicators="performanceIndicators"
                :series="performanceSeries"
              />
              <p v-else class="px-4 py-6 text-sm text-neutral-500">Keine ausreichenden Leistungsdaten verfügbar.</p>
            </div>
            <div class="w-full @lg:w-[calc(50%-0.75rem)]">
              <p class="text-sm font-medium text-center mt-4 p-0">Boostmultiplikatoren</p>
              <ChartsRadarChart
                v-if="performanceBoostMultiplierStats.length >= 3"
                :height="260"
                :indicators="performanceBoostMultiplierIndicators"
                :series="performanceBoostMultiplierSeries"
              />
              <p v-else class="px-4 py-6 text-sm text-neutral-500">Keine ausreichenden Leistungsdaten verfügbar.</p>
            </div>
          </div>
        </div>
      </template>

      <template #engine>
        <div class="@container mx-auto w-full max-w-2xl">
          <div v-if="rampStats.length" class="grid grid-cols-3 gap-4 px-4 mb-4">
            <ShipexkursStatHero
              v-for="stat in rampStats"
              :key="stat.label"
              :label="stat.label"
              :value="stat.value"
              :unit="stat.unit"
            />
          </div>
          <div v-if="thrusterGForceCategories.length || accelCategories.length" class="flex flex-wrap gap-6">
            <div v-if="thrusterGForceCategories.length" class="w-full @lg:w-[calc(50%-0.75rem)]">
              <p class="mt-4 flex items-center justify-center gap-2 p-0 text-center text-sm font-medium">
                <ShipexkursSystemIcon name="mainthruster" class="size-4 text-aris-400" />
                G-Kraft nach Triebwerkstyp
              </p>
              <ChartsBarChart :height="240" :categories="thrusterGForceCategories" :series="thrusterGForceSeries" unit="g" />
            </div>
            <div v-if="accelCategories.length" class="w-full @lg:w-[calc(50%-0.75rem)]">
              <p class="text-sm font-medium text-center mt-4 p-0">Beschleunigung & Bremsung</p>
              <ChartsBarChart :height="240" :categories="accelCategories" :series="accelSeries" unit="s" />
            </div>
          </div>
          <p v-if="!thrusterGForceCategories.length && !accelCategories.length && !rampStats.length" class="px-4 py-6 text-sm text-neutral-500">Keine Triebwerksdaten verfügbar.</p>
        </div>
      </template>

      <template #other>
        <div class="@container mx-auto w-full max-w-2xl">
          <div v-if="quantumStats.length || insuranceStats.length" class="flex flex-wrap gap-6">
            <div v-if="quantumStats.length" class="w-full @lg:w-[calc(50%-0.75rem)]">
              <p class="mt-4 mb-3 flex items-center justify-center gap-2 p-0 text-center text-sm font-medium">
                <ShipexkursSystemIcon name="quantumdrive" class="size-4 text-aris-400" />
                Quantenantrieb
              </p>
              <div class="grid grid-cols-2 gap-4 px-4">
                <ShipexkursStatHero
                  v-for="stat in quantumStats"
                  :key="stat.label"
                  :label="stat.label"
                  :value="stat.value"
                  :unit="stat.unit"
                />
              </div>
            </div>
            <div v-if="insuranceStats.length" class="w-full @lg:w-[calc(50%-0.75rem)]">
              <p class="text-sm font-medium text-center mt-4 mb-3 p-0">Versicherung</p>
              <div class="grid grid-cols-3 gap-4 px-4">
                <ShipexkursStatHero
                  v-for="stat in insuranceStats"
                  :key="stat.label"
                  :label="stat.label"
                  :value="stat.value"
                  :unit="stat.unit"
                />
              </div>
            </div>
          </div>
          <p v-else class="px-4 py-6 text-sm text-neutral-500">Keine weiteren Leistungsdaten verfügbar.</p>
        </div>
      </template>
    </UTabs>
  </div>
</template>
